import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient } from "../utils/apiClient";
import { showToast } from "../components/Toast";
import { useTranslation } from "react-i18next";
import "./Auth.css";

const SECTOR_OPTIONS = [
  "Agriculture",
  "Banking & Finance",
  "Construction",
  "Education",
  "Energy",
  "Healthcare",
  "Hospitality & Tourism",
  "IT & Technology",
  "Manufacturing",
  "Mining",
  "Real Estate",
  "Retail & Commerce",
  "Telecommunications",
  "Transportation & Logistics",
  "Other",
];

function SetupPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profile, setProfile] = useState({
    identification_id: "",
    phone: "",
    region: "",
    sector: "",
    terms_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleProfileNext = () => {
    if (!profile.identification_id || profile.identification_id.length < 10) {
      showToast.error(
        t(
          "setupProfile.nationalIdError",
          "Please enter a valid National ID (at least 10 digits)",
        ),
      );
      return;
    }
    if (!profile.phone) {
      showToast.error(
        t("setupProfile.phoneRequired", "Phone number is required"),
      );
      return;
    }
    if (!profile.sector) {
      showToast.error(
        t("setupProfile.sectorRequired", "Please select your sector"),
      );
      return;
    }
    if (!profile.terms_accepted) {
      showToast.error(
        t(
          "setupProfile.termsRequired",
          "You must accept the Terms & Privacy Policy",
        ),
      );
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || password.length < 8) {
      showToast.error(
        t(
          "setupProfile.passwordTooShort",
          "Password must be at least 8 characters",
        ),
      );
      return;
    }

    if (password !== confirmPassword) {
      showToast.error(
        t("setupProfile.passwordsMismatch", "Passwords do not match"),
      );
      return;
    }

    try {
      setLoading(true);
      await apiClient.post(
        `/auth/activate/${uid}/${token}/`,
        {
          password,
          identification_id: profile.identification_id,
          phone: profile.phone,
          region: profile.region,
          sector: profile.sector,
          terms_accepted: profile.terms_accepted,
        },
        { requiresAuth: false },
      );
      showToast.success(
        t(
          "setupProfile.activationSuccess",
          "Account activated successfully! You can now login.",
        ),
      );
      navigate("/login");
    } catch (err) {
      showToast.error(
        err.message || t("setupProfile.activationFailed", "Activation failed"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container" style={{ maxWidth: 480 }}>
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <div className="logo-icon">DSTL</div>
          </Link>
          <h1>
            {step === 1
              ? t("setupProfile.titleProfile", "Complete Your Profile")
              : t("setupProfile.titlePassword", "Set Your Password")}
          </h1>
          <p>
            {step === 1
              ? t(
                  "setupProfile.step1",
                  "Step 1 of 2 — Fill in your profile information",
                )
              : t(
                  "setupProfile.step2",
                  "Step 2 of 2 — Create a secure password",
                )}
          </p>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "center",
              marginTop: 12,
            }}
          >
            {[1, 2].map((s) => (
              <div
                key={s}
                style={{
                  width: 40,
                  height: 4,
                  borderRadius: 2,
                  background: s <= step ? "var(--primary-blue)" : "#ddd",
                }}
              />
            ))}
          </div>
        </div>

        {step === 1 ? (
          <div className="auth-form" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="form-group">
              <label>
                {t("profile.nationalId", "National ID *")}
              </label>
              <input
                type="text"
                value={profile.identification_id}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, identification_id: e.target.value }))
                }
                placeholder="e.g. 1199880012345"
              />
            </div>
            <div className="form-group">
              <label>
                {t("setupProfile.phoneLabel", "Phone Number *")}
              </label>
              <input
                type="text"
                value={profile.phone}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, phone: e.target.value }))
                }
                placeholder="+250 xxx xxx xxx"
              />
            </div>
            <div className="form-group">
              <label>{t("setupProfile.region", "Region")}</label>
              <input
                type="text"
                value={profile.region}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, region: e.target.value }))
                }
                placeholder="e.g. Kigali, Southern Province"
              />
            </div>
            <div className="form-group">
              <label>{t("setupProfile.sector", "Sector *")}</label>
              <select
                value={profile.sector}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, sector: e.target.value }))
                }
                style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--border-grey, #ddd)" }}
              >
                <option value="">
                  {t("setupProfile.selectSector", "Select sector...")}
                </option>
                {SECTOR_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid var(--border-grey, #e5e7eb)",
                background: "#f9fafb",
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                fontSize: 13,
                color: "var(--text-grey)",
              }}
            >
              <input
                type="checkbox"
                checked={profile.terms_accepted}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, terms_accepted: e.target.checked }))
                }
                style={{ marginTop: 4 }}
              />
              <div>
                <strong>
                  {t("profile.privacyDataUse", "Privacy & Data Use")}
                </strong>
                <p style={{ margin: "4px 0" }}>
                  {t(
                    "setupProfile.privacyText1",
                    "We securely store your identification, contact, and business details only to provide analytics, reports, and support inside this platform. We do not sell or share your data with third parties without your permission.",
                  )}
                </p>
                <p style={{ margin: "4px 0" }}>
                  {t(
                    "setupProfile.privacyText2",
                    "If you need to review or update your information, you can contact support at any time.",
                  )}
                </p>
                <p style={{ margin: "4px 0", fontWeight: 500 }}>
                  {t(
                    "setupProfile.agreeTerms",
                    "I agree to the Terms & Privacy Policy.",
                  )}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={handleProfileNext}
            >
              {t("common.continue", "Continue")}
            </button>
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t("auth.password", "Password")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t(
                  "setupProfile.passwordPlaceholder",
                  "Enter password (min 8 characters)",
                )}
                required
              />
            </div>

            <div className="form-group">
              <label>
                {t("setupProfile.confirmPassword", "Confirm Password")}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t(
                  "setupProfile.confirmPasswordPlaceholder",
                  "Confirm password",
                )}
                required
              />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setStep(1)}
              >
                {t("common.back", "Back")}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ flex: 1 }}
              >
                {loading
                  ? t("setupProfile.activating", "Activating...")
                  : t("setupProfile.activateAccount", "Activate Account")}
              </button>
            </div>
          </form>
        )}

        <div className="auth-footer">
          <p>
            {t("setupProfile.alreadyHaveAccount", "Already have an account?")}{" "}
            <Link to="/login" className="link">
              {t("auth.signIn")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SetupPassword;
